import { encounterField } from '../../shared/array-types';
import type { BaseEnvelope, BasePatchOp } from './base-patch';
import type { AppliedFrame, AppliedTouchedRow } from '../transport/applied-frame';

type Row = Record<string, unknown>;
const record = (value: unknown): value is Row => value !== null && typeof value === 'object' && !Array.isArray(value);
const upper = (value: unknown): string => String(value ?? '').toUpperCase();
const shapeColumns = (row: Row): boolean => Object.prototype.hasOwnProperty.call(row, 'encounter_shape')
    && Object.keys(row).every(key => key === 'encounter_id' || key === 'encounter_shape')
    && (row.encounter_shape === null || typeof row.encounter_shape === 'string');

/** Geometry consumers own the shape's consequences. The encounter compiler only
 * carries this saved field, so a lone shape save patches its resident shell. */
export function savedEncounterShapeRows(base: BaseEnvelope['base'], ops: readonly BasePatchOp[]): AppliedTouchedRow[] | null {
    const id = upper(encounterField(base, 'encounter_id'));
    if (!id || !record(base.encounters) || ops.length !== 1) return null;
    const op = ops[0]!;
    if (op.table !== 'encounters' || op.op !== 'upsert' || op.inputs || op.catalog_inputs
        || upper(op.entity_id) !== id || upper(op.row_id) !== id
        || !shapeColumns(op.cols) || upper(op.cols.encounter_id) !== id) return null;
    return [{ table: 'encounters', row_id: id, partial: true, row: op.cols }];
}

/** Exact root-only frame contract: no member, inventory, status or computed work
 * may be hidden behind the fast path. */
export function encounterShapeOnlyRow(frame: AppliedFrame): AppliedTouchedRow | null {
    if (!frame.operation_id.startsWith('encounter-shape:') || frame.rows.length !== 1
        || frame.new_entities.length || frame.deletes.length || frame.new_inventory?.length
        || Object.keys(frame.member_revs).length || Object.keys(frame.computed).length
        || frame.computed_delta && Object.keys(frame.computed_delta).length || frame.inputs) return null;
    const row = frame.rows[0]!;
    const id = upper(frame.encounter_id);
    return row.table === 'encounters' && row.partial === true && upper(row.row_id) === id
        && shapeColumns(row.row) && upper(row.row.encounter_id) === id
        && row.base === undefined && row.node === undefined && row.position === undefined ? row : null;
}

/** Copy only the changed shell ancestors; every roster, inventory and catalog
 * remains the same held object. Inventory-only facets have no shell to patch. */
export function patchEncounterShapeRow(source: unknown, row: AppliedTouchedRow, raw = false): unknown {
    if (!record(source) || !record(source.encounters)
        || upper(encounterField(source, 'encounter_id')) !== upper(row.row_id)) return source;
    const bucket = source.encounters;
    const shape = row.row.encounter_shape;
    if (raw && shape === null) {
        if (!Object.prototype.hasOwnProperty.call(bucket, 'encounter_shape')) return source;
        const next = { ...bucket };
        delete next.encounter_shape;
        return { ...source, encounters: next };
    }
    return bucket.encounter_shape === shape ? source : { ...source, encounters: { ...bucket, encounter_shape: shape } };
}
